(() => {
  const PRICE_SORTS = new Set(['price-ascending', 'price-descending']);
  const ALPHA_SORTS = new Set(['title-ascending', 'title-descending']);
  const DATE_SORTS = new Set(['created-ascending', 'created-descending']);
  const COLLECTION_PARAM = 'gr_collection';
  const PRICE_PARAM = 'gr_price';
  const AVAILABILITY_PARAM = 'gr_availability';
  const NATIVE_AVAILABILITY_PARAM = 'filter.v.availability';
  const FETCH_CONCURRENCY = 5;

  let latestRun = 0;
  let observer = null;
  let isRendering = false;
  const sourceCache = new Map();

  function params() {
    return new URLSearchParams(window.location.search);
  }

  function currentSort() {
    return params().get('sort_by') || '';
  }

  function selectedCollections() {
    return Array.from(new Set(params().getAll(COLLECTION_PARAM).filter(Boolean)));
  }

  function selectedPriceBands() {
    return new Set(params().getAll(PRICE_PARAM).filter(Boolean));
  }

  function selectedAvailability() {
    const value = params().get(AVAILABILITY_PARAM) || '';
    if (value === 'in' || value === 'out') return value;
    return '';
  }

  function hasCustomFilters() {
    return selectedCollections().length > 0 || selectedPriceBands().size > 0 || selectedAvailability() !== '';
  }

  function needsCustomProcessing() {
    return hasCustomFilters() || PRICE_SORTS.has(currentSort());
  }

  function grid() {
    return document.querySelector('#product-grid[data-gr-available-price-grid="true"]');
  }

  function productKey(item) {
    const handle = item.dataset.grProductHandle;
    if (handle) return handle;

    const link = item.querySelector('a[href*="/products/"]');
    if (!link) return item.textContent || Math.random().toString(36);

    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      return url.pathname.replace(/\/collections\/[^/]+\/products\//, '/products/');
    } catch (error) {
      return link.getAttribute('href');
    }
  }

  function itemData(item, index, sourceHandle = '') {
    const card = item.querySelector('product-card[data-available-min-price]');
    if (!card) return null;

    const rawPrice = card.dataset.availableMinPrice;
    const availablePrice = parseInt(rawPrice || '', 10);
    const available = card.dataset.productAvailable !== 'false';
    const variantPrices = (item.dataset.grProductPrices || '')
      .split(',')
      .map((value) => parseInt(value, 10))
      .filter(Number.isFinite);

    const title = item.dataset.grProductTitle || item.querySelector('.product-card-title, .product-card__title, h3, h4')?.textContent?.trim() || '';
    const publishedAt = parseInt(item.dataset.grProductPublishedAt || '0', 10) || 0;

    return {
      item,
      index,
      key: productKey(item),
      available,
      availablePrice: Number.isFinite(availablePrice) ? availablePrice : Number.MAX_SAFE_INTEGER,
      variantPrices,
      title,
      publishedAt,
      sourceHandle
    };
  }

  function itemsFromDocument(doc, sourceHandle = '') {
    return Array.from(doc.querySelectorAll('#product-grid > .column[data-gr-product-card]'))
      .map((item, index) => itemData(item, index, sourceHandle))
      .filter(Boolean);
  }

  function itemsFromGrid(activeGrid) {
    return Array.from(activeGrid.querySelectorAll(':scope > .column[data-gr-product-card]'))
      .map((item, index) => itemData(item, index))
      .filter(Boolean);
  }

  function priceBreaks(activeGrid) {
    const sectionId = activeGrid.dataset.id;
    const section = sectionId ? document.getElementById(`CollectionSection-${sectionId}`) : null;
    const defaults = [25, 50, 100, 150];
    if (!section) return defaults;

    return [
      section.dataset.grPriceBreak1,
      section.dataset.grPriceBreak2,
      section.dataset.grPriceBreak3,
      section.dataset.grPriceBreak4
    ].map((value, index) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : defaults[index];
    });
  }

  function matchesPriceBands(entry, activeGrid) {
    const bands = selectedPriceBands();
    if (!bands.size) return true;

    const breaks = priceBreaks(activeGrid);
    const ranges = [
      [0, breaks[0]],
      [breaks[0], breaks[1]],
      [breaks[1], breaks[2]],
      [breaks[2], breaks[3]],
      [breaks[3], null]
    ];

    const prices = entry.variantPrices.length
      ? entry.variantPrices
      : (Number.isFinite(entry.availablePrice) ? [entry.availablePrice] : []);

    return prices.some((priceCents) => {
      const price = priceCents / 100;
      return Array.from(bands).some((band) => {
        const range = ranges[parseInt(band, 10)];
        if (!range) return false;
        const [min, max] = range;
        return price >= min && (max === null || price < max);
      });
    });
  }

  function matchesAvailability(entry) {
    const selected = selectedAvailability();
    if (!selected) return true;
    return selected === 'in' ? entry.available : !entry.available;
  }

  function filteredItems(items, activeGrid) {
    return items.filter((entry) => matchesAvailability(entry) && matchesPriceBands(entry, activeGrid));
  }

  function sortedItems(items) {
    const sort = currentSort();
    if (!sort) return items.slice();

    if (PRICE_SORTS.has(sort)) {
      const direction = sort === 'price-descending' ? -1 : 1;
      return items.slice().sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        const priceDiff = (a.availablePrice - b.availablePrice) * direction;
        if (priceDiff !== 0) return priceDiff;
        return a.index - b.index;
      });
    }

    /* Shopify already supplies a correctly sorted order when one collection is
       selected. For multiple selected collections, merge the deterministic
       sorts we can reproduce globally. */
    if (selectedCollections().length > 1 && ALPHA_SORTS.has(sort)) {
      const direction = sort === 'title-descending' ? -1 : 1;
      return items.slice().sort((a, b) => a.title.localeCompare(b.title) * direction || a.index - b.index);
    }

    if (selectedCollections().length > 1 && DATE_SORTS.has(sort)) {
      const direction = sort === 'created-descending' ? -1 : 1;
      return items.slice().sort((a, b) => (a.publishedAt - b.publishedAt) * direction || a.index - b.index);
    }

    return items.slice();
  }

  function cleanSourceParams(pageNumber) {
    const search = params();
    search.delete(COLLECTION_PARAM);
    search.delete(PRICE_PARAM);
    search.delete(AVAILABILITY_PARAM);
    search.delete(NATIVE_AVAILABILITY_PARAM);
    search.delete('section_id');
    search.set('page', String(pageNumber));
    return search;
  }

  function sourceUrl(pathname, pageNumber) {
    const search = cleanSourceParams(pageNumber);
    return `${pathname}?${search.toString()}`;
  }

  async function fetchDocument(pathname, pageNumber) {
    const response = await fetch(sourceUrl(pathname, pageNumber), {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Product page request failed with ${response.status}`);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function cacheKeyForPath(pathname) {
    const search = cleanSourceParams(1);
    search.delete('page');
    const normalized = new URLSearchParams();
    Array.from(search.entries())
      .sort(([aKey, aValue], [bKey, bValue]) => {
        const keyCompare = aKey.localeCompare(bKey);
        return keyCompare || aValue.localeCompare(bValue);
      })
      .forEach(([key, value]) => normalized.append(key, value));
    return `${pathname}?${normalized.toString()}`;
  }

  function rememberCache(key, items) {
    sourceCache.set(key, items);
    while (sourceCache.size > 8) {
      sourceCache.delete(sourceCache.keys().next().value);
    }
  }

  async function loadAllItemsForPath(pathname, sourceHandle = '') {
    const key = cacheKeyForPath(pathname);
    if (sourceCache.has(key)) {
      return sourceCache.get(key).map((entry) => ({ ...entry, item: entry.item.cloneNode(true) }));
    }

    const firstDoc = await fetchDocument(pathname, 1);
    const firstGrid = firstDoc.getElementById('product-grid');
    if (!firstGrid) throw new Error(`No product grid found for ${pathname}`);

    const totalPages = Math.max(parseInt(firstGrid.dataset.totalPages || '1', 10), 1);
    const collected = itemsFromDocument(firstDoc, sourceHandle);

    if (totalPages > 1) {
      const pages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
      for (let start = 0; start < pages.length; start += FETCH_CONCURRENCY) {
        const batch = pages.slice(start, start + FETCH_CONCURRENCY);
        const docs = await Promise.all(batch.map((page) => fetchDocument(pathname, page)));
        docs.forEach((doc) => collected.push(...itemsFromDocument(doc, sourceHandle)));
      }
    }

    const seen = new Set();
    const unique = [];
    collected.forEach((entry) => {
      if (!entry || seen.has(entry.key)) return;
      seen.add(entry.key);
      unique.push({ ...entry, index: unique.length });
    });

    /* Store detached clones so later renders cannot mutate the cached source. */
    const cached = unique.map((entry) => ({ ...entry, item: entry.item.cloneNode(true) }));
    rememberCache(key, cached);
    return unique;
  }

  async function loadSelectedCollectionItems() {
    const handles = selectedCollections();
    const all = [];

    for (let start = 0; start < handles.length; start += FETCH_CONCURRENCY) {
      const batch = handles.slice(start, start + FETCH_CONCURRENCY);
      const results = await Promise.all(batch.map((handle) =>
        loadAllItemsForPath(`/collections/${encodeURIComponent(handle)}`, handle)
      ));
      all.push(...results.flat());
    }

    const seen = new Set();
    const unique = [];
    all.forEach((entry) => {
      if (!entry || seen.has(entry.key)) return;
      seen.add(entry.key);
      unique.push({ ...entry, index: unique.length });
    });
    return unique;
  }

  async function loadCurrentCollectionItems(activeGrid) {
    const pathname = window.location.pathname;
    const key = cacheKeyForPath(pathname);

    if (sourceCache.has(key)) {
      return sourceCache.get(key).map((entry) => ({ ...entry, item: entry.item.cloneNode(true) }));
    }

    const totalPages = Math.max(parseInt(activeGrid.dataset.totalPages || '1', 10), 1);
    let collected = itemsFromGrid(activeGrid);

    if (totalPages > 1) {
      const pages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
      for (let start = 0; start < pages.length; start += FETCH_CONCURRENCY) {
        const batch = pages.slice(start, start + FETCH_CONCURRENCY);
        const docs = await Promise.all(batch.map((page) => fetchDocument(pathname, page)));
        docs.forEach((doc) => collected.push(...itemsFromDocument(doc)));
      }
    }

    const seen = new Set();
    const unique = [];
    collected.forEach((entry) => {
      if (!entry || seen.has(entry.key)) return;
      seen.add(entry.key);
      unique.push({ ...entry, index: unique.length });
    });

    rememberCache(key, unique.map((entry) => ({ ...entry, item: entry.item.cloneNode(true) })));
    return unique;
  }

  async function loadSourceItems(activeGrid) {
    if (selectedCollections().length) return loadSelectedCollectionItems();
    return loadCurrentCollectionItems(activeGrid);
  }

  function clearGrid(activeGrid) {
    activeGrid.querySelectorAll(':scope > .column, :scope > .gr-custom-filter-empty').forEach((item) => item.remove());
  }

  function originalPagination() {
    const activeGrid = grid();
    return activeGrid?.closest('.collection-main')?.querySelector('pagination-theme') || null;
  }

  function customPagination() {
    return document.getElementById('grAvailablePricePagination');
  }

  function cleanupPagination() {
    const pagination = originalPagination();
    const custom = customPagination();
    if (pagination) pagination.style.display = '';
    if (custom) custom.remove();
  }

  function renderPagination(activeGrid, items, currentPage, paginationType) {
    const perPage = Math.max(parseInt(activeGrid.dataset.productsPerPage || '24', 10), 1);
    const totalPages = Math.ceil(items.length / perPage);
    const nativePagination = originalPagination();
    let container = customPagination();

    if (nativePagination) nativePagination.style.display = 'none';

    if (!container) {
      container = document.createElement('div');
      container.id = 'grAvailablePricePagination';
      container.className = 'gr-available-price-pagination';
      (nativePagination || activeGrid).insertAdjacentElement('afterend', container);
    }

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    if (paginationType === 'loadmore' || paginationType === 'infinite') {
      const shown = Math.min(currentPage * perPage, items.length);
      const hasMore = shown < items.length;
      container.innerHTML = hasMore
        ? '<div class="row"><div class="small-12 columns"><button class="load-more button" type="button"><span>Load more</span></button></div></div>'
        : '';
      const button = container.querySelector('.load-more');
      if (button) button.addEventListener('click', () => renderPage(activeGrid, items, currentPage + 1, true));
      return;
    }

    const parts = [];
    parts.push(`<span class="prev"><a href="#" data-gr-available-page="${Math.max(currentPage - 1, 1)}" ${currentPage === 1 ? 'aria-disabled="true"' : ''}>‹<span class="visually-hidden">Previous</span></a></span>`);

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 2) {
        parts.push(`<span class="page ${i === currentPage ? 'current' : ''}">${i === currentPage ? i : `<a href="#" data-gr-available-page="${i}">${i}</a>`}</span>`);
      } else if (i === 2 || i === totalPages - 1) {
        parts.push('<span class="page dots">…</span>');
      }
    }

    parts.push(`<span class="next"><a href="#" data-gr-available-page="${Math.min(currentPage + 1, totalPages)}" ${currentPage === totalPages ? 'aria-disabled="true"' : ''}>›<span class="visually-hidden">Next</span></a></span>`);
    container.innerHTML = `<div class="pagination pagination-type--paginated"><div class="row"><div class="small-12 columns"><div class="page-numbers nav-links">${parts.join('')}</div></div></div></div>`;

    container.querySelectorAll('[data-gr-available-page]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true') return;
        renderPage(activeGrid, items, parseInt(link.dataset.grAvailablePage, 10), true);
        activeGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function renderEmptyState(activeGrid) {
    const empty = document.createElement('li');
    empty.className = 'gr-custom-filter-empty';
    empty.innerHTML = '<div class="collection-empty"><p>No products match the selected filters.</p></div>';
    activeGrid.appendChild(empty);
  }

  function updateCustomProductCount(count) {
    if (!hasCustomFilters()) return;
    const text = `${count} ${count === 1 ? 'product' : 'products'}`;
    document.querySelectorAll('.thb-filter-count .facets__label').forEach((label) => {
      label.textContent = text;
    });
  }

  function renderPage(activeGrid, items, pageNumber, pushState = false) {
    const perPage = Math.max(parseInt(activeGrid.dataset.productsPerPage || '24', 10), 1);
    const paginationType = activeGrid.dataset.paginationType || 'paginated';
    const totalPages = Math.max(Math.ceil(items.length / perPage), 1);
    const page = Math.min(Math.max(parseInt(pageNumber || '1', 10), 1), totalPages);
    const start = (page - 1) * perPage;
    const pageItems = (paginationType === 'loadmore' || paginationType === 'infinite')
      ? items.slice(0, page * perPage)
      : items.slice(start, start + perPage);

    isRendering = true;
    clearGrid(activeGrid);

    if (pageItems.length) {
      const fragment = document.createDocumentFragment();
      pageItems.forEach((entry) => fragment.appendChild(entry.item.cloneNode(true)));
      activeGrid.appendChild(fragment);
    } else {
      renderEmptyState(activeGrid);
    }

    renderPagination(activeGrid, items, page, paginationType);
    updateCustomProductCount(items.length);

    if (pushState) {
      const url = new URL(window.location.href);
      if (page > 1) url.searchParams.set('page', page);
      else url.searchParams.delete('page');
      window.history.pushState({ grAvailablePricePage: page }, '', url.toString());
    }

    window.setTimeout(() => {
      isRendering = false;
    }, 0);
  }

  function restoreNativeGrid(activeGrid) {
    cleanupPagination();
    if (activeGrid?.dataset.grCustomRendered !== 'true') return;

    const search = params();
    search.delete(COLLECTION_PARAM);
    search.delete(PRICE_PARAM);
    search.delete(AVAILABILITY_PARAM);
    search.delete(NATIVE_AVAILABILITY_PARAM);
    search.delete('page');
    const query = search.toString();

    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(query, null, false);
    } else {
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
      window.location.assign(nextUrl);
    }
  }

  async function runCustomGrid() {
    const activeGrid = grid();
    if (!activeGrid) return;

    if (!needsCustomProcessing()) {
      restoreNativeGrid(activeGrid);
      return;
    }

    const runId = ++latestRun;
    const currentPage = Math.max(parseInt(params().get('page') || '1', 10), 1);
    const nativePagination = originalPagination();
    if (nativePagination) nativePagination.style.display = 'none';
    activeGrid.classList.add('loading');

    try {
      const sourceItems = await loadSourceItems(activeGrid);
      if (runId !== latestRun) return;

      const filtered = filteredItems(sourceItems, activeGrid);
      const finalItems = sortedItems(filtered);
      activeGrid.dataset.grCustomRendered = 'true';
      renderPage(activeGrid, finalItems, currentPage);
    } catch (error) {
      console.warn('Gear Rack custom collection/price filtering could not finish:', error);
      cleanupPagination();
    } finally {
      activeGrid.classList.remove('loading');
    }
  }

  function observeFacetRenders() {
    const target = document.getElementById('ProductGridContainer');
    if (!target || observer) return;

    observer = new MutationObserver(() => {
      if (isRendering) return;
      window.clearTimeout(observeFacetRenders.timeout);
      observeFacetRenders.timeout = window.setTimeout(runCustomGrid, 120);
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  function initialize() {
    observeFacetRenders();
    runCustomGrid();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.addEventListener('gear-rack:custom-filters-changed', () => {
    window.setTimeout(runCustomGrid, 0);
  });

  window.addEventListener('gear-rack:facets-rendered', () => {
    window.setTimeout(runCustomGrid, 50);
  });

  window.addEventListener('popstate', () => window.setTimeout(runCustomGrid, 50));

  document.addEventListener('shopify:section:load', () => {
    observer = null;
    observeFacetRenders();
    runCustomGrid();
  });
})();
